import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  Avatar,
  Rating
} from '@mui/material';
import {
  AccountBalanceWallet,
  TrendingUp,
  Insights,
  AutoGraph,
  Security,
  Speed,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Logo from './common/Logo';
import SEOHead from './common/SEOHead';

const MarketingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: <AccountBalanceWallet sx={{ fontSize: 48 }} />,
      title: "Smart Budgeting",
      description: "Create intelligent budgets that adapt to your spending patterns and help you save more effectively.",
      benefits: ["Zero-based budgeting", "Automatic categorization", "Spending insights"]
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48 }} />,
      title: "Investment Tracking",
      description: "Monitor your portfolio performance and track progress toward your financial goals in real-time.",
      benefits: ["Portfolio analysis", "Goal tracking", "Performance metrics"]
    },
    {
      icon: <Insights sx={{ fontSize: 48 }} />,
      title: "Financial Insights",
      description: "Get personalized insights and recommendations to optimize your financial health and spending habits.",
      benefits: ["AI-powered insights", "Spending analysis", "Custom recommendations"]
    },
    {
      icon: <AutoGraph sx={{ fontSize: 48 }} />,
      title: "Expense Analytics",
      description: "Visualize your spending patterns with beautiful charts and discover opportunities to save money.",
      benefits: ["Interactive charts", "Trend analysis", "Category breakdown"]
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: "Bank-Level Security",
      description: "Your financial data is protected with enterprise-grade encryption and security measures.",
      benefits: ["256-bit encryption", "Secure authentication", "Privacy focused"]
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: "Real-Time Sync",
      description: "Access your financial data instantly across all devices with real-time synchronization.",
      benefits: ["Cloud sync", "Multi-device access", "Offline support"]
    }
  ];

  const testimonials = [
    {
      name: "Amanda Thompson",
      role: "Small Business Owner",
      avatar: "A",
      rating: 5,
      comment: "Altifi helped me save $12,000 in my first year! The insights are incredible and the interface is so intuitive."
    },
    {
      name: "Marcus Chen",
      role: "Software Engineer",
      avatar: "M",
      rating: 5,
      comment: "Finally, a finance app that actually understands how I spend money. The budgeting features are game-changing."
    },
    {
      name: "Sarah Williams",
      role: "Teacher",
      avatar: "S",
      rating: 5,
      comment: "I've tried every budgeting app out there. Altifi is the first one that made budgeting feel effortless and fun."
    }
  ];

  const stats = [
    { value: "50K+", label: "Active Users" },
    { value: "$2.5M+", label: "Money Saved" },
    { value: "4.9★", label: "User Rating" },
    { value: "98%", label: "Success Rate" }
  ];

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const marketingStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "altifi - Smart Personal Finance Management",
    "description": "Take control of your financial future with altifi. Smart budgeting, investment tracking, and financial insights all in one beautiful app.",
    "url": "https://altifi.com",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="altifi - Smart Personal Finance Management | Budget, Save, Invest"
        description="Take control of your financial future with altifi. Smart budgeting, investment tracking, and financial insights all in one beautiful app. Join 50K+ users saving money."
        keywords="personal finance, budgeting app, expense tracking, investment tracking, financial planning, money management, budget tracker"
        canonicalUrl="https://altifi.com"
        structuredData={marketingStructuredData}
      />
      
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #0F1410 0%, #1A1F1A 50%, #242924 100%)'
            : 'linear-gradient(135deg, #7C8B6F 0%, #A4B494 50%, #8B7C8B 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Navigation */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2
              }}
            >
              <Logo width={120} height={42} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLogin}
                  sx={{ textTransform: 'none' }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={handleGetStarted}
                  sx={{ textTransform: 'none' }}
                >
                  Get Started
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Hero Section */}
        <Container maxWidth="xl" sx={{ pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 12 } }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'white',
                maxWidth: 1000,
                mx: 'auto',
                mb: 8
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                  fontWeight: 700,
                  mb: 3,
                  lineHeight: 1.1,
                  textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.02em'
                }}
              >
                Take Control of Your{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(45deg, #FFE55C, #FFB347)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                  }}
                >
                  Financial Future
                </Box>
              </Typography>
              
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  fontWeight: 400,
                  mb: 4,
                  opacity: 0.95,
                  lineHeight: 1.4,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                Altifi helps you budget smarter, save more, and invest with confidence.
                <br />
                Join thousands who've transformed their financial lives.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={3} 
                justifyContent="center"
                sx={{ mb: 6 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForward />}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(45deg, #FFB347, #FFE55C)',
                    color: '#2C3E2D',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FF9E2A, #FFD700)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(255, 179, 71, 0.4)'
                    }
                  }}
                >
                  Start Free Today
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleLogin}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Sign In
                </Button>
              </Stack>

              {/* Stats */}
              <Grid container spacing={4} justifyContent="center">
                {stats.map((stat, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            color: '#FFE55C',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            fontSize: { xs: '2rem', sm: '2.5rem' }
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 500,
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </motion.div>
        </Container>

        {/* Features Section */}
        <Box
          sx={{
            background: alpha(theme.palette.background.default, 0.95),
            backdropFilter: 'blur(20px)',
            py: { xs: 8, md: 12 }
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    mb: 2,
                    color: theme.palette.text.primary
                  }}
                >
                  Everything You Need to Succeed
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    color: theme.palette.text.secondary,
                    maxWidth: 600,
                    mx: 'auto'
                  }}
                >
                  Powerful tools and insights designed to help you achieve your financial goals
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.6)})`,
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            mb: 3,
                            color: 'white'
                          }}
                        >
                          {feature.icon}
                        </Box>
                        
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: theme.palette.text.primary
                          }}
                        >
                          {feature.title}
                        </Typography>
                        
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 3,
                            color: theme.palette.text.secondary,
                            lineHeight: 1.6
                          }}
                        >
                          {feature.description}
                        </Typography>

                        <Stack spacing={1}>
                          {feature.benefits.map((benefit, benefitIndex) => (
                            <Box key={benefitIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle 
                                sx={{ 
                                  fontSize: 18, 
                                  color: theme.palette.primary.main 
                                }} 
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                {benefit}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1A1F1A 0%, #242924 100%)'
              : 'linear-gradient(135deg, #7C8B6F 0%, #8B7C8B 100%)',
            color: 'white'
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    mb: 2,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  Loved by Thousands
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    opacity: 0.9,
                    maxWidth: 600,
                    mx: 'auto',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  See what our users are saying about their financial transformation
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Avatar
                            sx={{
                              width: 60,
                              height: 60,
                              background: 'linear-gradient(45deg, #FFB347, #FFE55C)',
                              color: '#2C3E2D',
                              fontWeight: 700,
                              fontSize: '1.5rem',
                              mr: 2
                            }}
                          >
                            {testimonial.avatar}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: 'white' }}
                            >
                              {testimonial.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                              {testimonial.role}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Rating
                          value={testimonial.rating}
                          readOnly
                          sx={{ mb: 2, color: '#FFE55C' }}
                        />
                        
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            lineHeight: 1.6,
                            fontStyle: 'italic'
                          }}
                        >
                          "{testimonial.comment}"
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: alpha(theme.palette.background.default, 0.95),
            backdropFilter: 'blur(20px)'
          }}
        >
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    mb: 3,
                    color: theme.palette.text.primary
                  }}
                >
                  Ready to Transform Your Finances?
                </Typography>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    mb: 4,
                    color: theme.palette.text.secondary,
                    maxWidth: 500,
                    mx: 'auto'
                  }}
                >
                  Join over 50,000 users who have taken control of their financial future with altifi
                </Typography>

                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={3} 
                  justifyContent="center"
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleLogin}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>

                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    No credit card required • Free forever • Cancel anytime
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default MarketingPage; 